"use client"

import type React from "react"
import { useState } from "react"
import { Sparkles, Clock, CalendarIcon, Users, MapPin, AlertCircle, CheckCircle } from "lucide-react"
import { generateTripSuggestions } from "../services/tripSuggestions"
import TravelPlanDisplay from "./TravelPlanDisplay"
import DatePicker from "./DatePicker"

const telanganaLocations = [
  "Hyderabad",
  "Warangal",
  "Nizamabad",
  "Karimnagar",
  "Khammam",
  "Mahbubnagar",
  "Nalgonda",
  "Adilabad",
  "Medak",
  "Rangareddy",
  "Siddipet",
  "Sangareddy",
  "Vikarabad",
  "Kamareddy",
  "Nagarkurnool",
]

interface TripData {
  startDate: Date | null
  endDate: Date | null
  travelers: string
  locations: string[]
}

interface ApiResponse {
  success: boolean
  suggestions: string
  error?: string
  metadata?: {
    duration: number
    travelers: number
    locations: string[]
    generatedAt: string
  }
}

const TripPlanner: React.FC = () => {
  const [tripData, setTripData] = useState<TripData>({
    startDate: null,
    endDate: null,
    travelers: "",
    locations: [],
  })
  const [suggestions, setSuggestions] = useState<string>("")
  const [tripMetadata, setTripMetadata] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>("")
  const [success, setSuccess] = useState(false)

  const handleLocationChange = (location: string, checked: boolean) => {
    setTripData((prev) => ({
      ...prev,
      locations: checked ? [...prev.locations, location] : prev.locations.filter((l) => l !== location),
    }))
  }

  const validateForm = (): string | null => {
    if (!tripData.startDate) return "Please select a start date"
    if (!tripData.endDate) return "Please select an end date"
    if (!tripData.travelers) return "Please select number of travelers"
    if (tripData.locations.length === 0) return "Please select at least one location"

    if (tripData.endDate <= tripData.startDate) {
      return "End date must be after start date"
    }

    return null
  }

  const handleGenerateSuggestions = async () => {
    // Clear previous states
    setError("")
    setSuccess(false)
    setSuggestions("")
    setTripMetadata(null)

    // Validate form
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)

    try {
      const result: ApiResponse = await generateTripSuggestions({
        startDate: tripData.startDate!.toISOString(),
        endDate: tripData.endDate!.toISOString(),
        travelers: Number.parseInt(tripData.travelers),
        locations: tripData.locations,
      })

      if (result.success) {
        setSuggestions(result.suggestions)
        setTripMetadata({
          ...result.metadata,
          startDate: tripData.startDate!.toISOString(),
          endDate: tripData.endDate!.toISOString(),
        })
        setSuccess(true)
        setError("")
      } else {
        setError(result.error || "Failed to generate suggestions")
        setSuggestions("")
      }
    } catch (error) {
      console.error("Unexpected error:", error)
      setError("An unexpected error occurred. Please try again.")
      setSuggestions("")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 lg:py-12"> */}
      <div className="max-w-7xl mx-auto px-0 py-0 sm:px-4 sm:py-8 lg:px-6 lg:py-12">
        {/* Hero Section */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
            <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
            AI-Powered Trip Planning
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            Plan Your Perfect
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 block sm:inline">
              {" "}
              Telangana Journey
            </span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
            Let our AI create a personalized itinerary that showcases the best of Telangana's rich culture, heritage,
            and natural beauty
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 border border-red-200 bg-red-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {success && !error && (
          <div className="mb-6 border border-green-200 bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-800">Your personalized itinerary has been generated successfully!</span>
            </div>
          </div>
        )}

        {/* Main Planning Card */}
        <div className="shadow-xl sm:shadow-2xl border-0 bg-white/80 backdrop-blur-sm mb-8 sm:mb-12 mx-2 sm:mx-0 rounded-lg">
          <div className="pb-6 sm:pb-8 px-4 sm:px-6 pt-6">
            <div className="flex items-center gap-2 sm:gap-3 text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              <div className="p-1.5 sm:p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg">
                <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              Trip Details
            </div>
            <p className="text-sm sm:text-base text-gray-600">
              Tell us about your travel preferences and we'll create the perfect itinerary
            </p>
          </div>

          <div className="space-y-6 sm:space-y-8 px-4 sm:px-6 pb-6">
            {/* Top Row - Dates and Travelers */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
              {/* Date Selection */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  <label className="text-base sm:text-lg font-semibold text-gray-800">When are you traveling?</label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-gray-600">Departure Date</label>
                    <DatePicker
                      selected={tripData.startDate}
                      onChange={(date) => setTripData((prev) => ({ ...prev, startDate: date }))}
                      placeholderText="Select start date"
                      minDate={new Date()}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-gray-600">Return Date</label>
                    <DatePicker
                      selected={tripData.endDate}
                      onChange={(date) => setTripData((prev) => ({ ...prev, endDate: date }))}
                      placeholderText="Select end date"
                      minDate={tripData.startDate || new Date()}
                    />
                  </div>
                </div>
              </div>

              {/* Travelers Count */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  <label className="text-base sm:text-lg font-semibold text-gray-800">Group Size</label>
                </div>

                <div className="space-y-2">
                  <label className="text-xs sm:text-sm font-medium text-gray-600">Number of Travelers</label>
                  <select
                    value={tripData.travelers}
                    onChange={(e) => setTripData((prev) => ({ ...prev, travelers: e.target.value }))}
                    className="w-full h-10 sm:h-12 bg-white border-2 border-gray-200 hover:border-blue-300 transition-all duration-200 rounded-md px-3 text-sm sm:text-base"
                  >
                    <option value="">Select group size</option>
                    <option value="1">1 Person</option>
                    <option value="2">2 People</option>
                    <option value="3">3 People</option>
                    <option value="4">4 People</option>
                    <option value="5">5 People</option>
                    <option value="6">6+ People</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Locations Selection */}
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                <label className="text-base sm:text-lg font-semibold text-gray-800">
                  Where would you like to visit?
                </label>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                  Select the destinations you'd like to explore in Telangana
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
                  {telanganaLocations.map((location) => (
                    <div
                      key={location}
                      className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-white rounded-lg hover:bg-blue-50 transition-colors duration-200 border border-gray-200 min-h-[44px] touch-manipulation"
                    >
                      <input
                        type="checkbox"
                        id={location}
                        checked={tripData.locations.includes(location)}
                        onChange={(e) => handleLocationChange(location, e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <label
                        htmlFor={location}
                        className="text-xs sm:text-sm font-medium text-gray-700 cursor-pointer flex-1 leading-tight"
                      >
                        {location}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex justify-center pt-4 sm:pt-6">
              <button
                onClick={handleGenerateSuggestions}
                disabled={isLoading}
                className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-12 text-base sm:text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                    <span className="hidden sm:inline">Generating Your Perfect Trip...</span>
                    <span className="sm:hidden">Generating...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">Get AI-Powered Suggestions</span>
                    <span className="sm:hidden">Get Suggestions</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* AI Suggestions */}
        {suggestions && <TravelPlanDisplay suggestions={suggestions} tripData={tripMetadata} />}
      </div>
    </div>
  )
}

export default TripPlanner
